import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Typography, Box, Switch, createTheme, ThemeProvider } from '@mui/material';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import TheaterComedyOutlinedIcon from '@mui/icons-material/TheaterComedyOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration } from '../../redux/configuration/types';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange: (payload: any) => void;
    setting: IUserConfiguration;
}

const AccountPreference: React.FC<ISettingsProps> = props => {
    const { onChange, setting } = props;
    const [settingValue, setSettingsValue] = useState<any>();

    useEffect(() => {
        if (setting) {
            setSettingsValue({
                personalized_controls: setting.personalized_controls || {
                    hls_play: 'inactive',
                    contest_emails: 'inactive',
                    ads: 'inactive',
                    audition: 'inactive',
                    movie_ticket: 'inactive',
                    voting: 'inactive',
                    acts: 'inactive',
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    const switchTheme = createTheme({
        components: {
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        // Controls default (unchecked) color for the thumb
                        color: theme[themeStyle].tag_white,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: theme[themeStyle].tag_white,
                        },
                    },
                    track: {
                        // Controls default (unchecked) color for the track
                        opacity: 1,
                        backgroundColor: theme[themeStyle].gray_message,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: 0.9,
                            backgroundColor: theme[themeStyle].highlight,
                        },
                    },
                },
            },
            //@ts-ignore
            custom: {},
        },
    });

    const handleOnToggle = (value: boolean, child: string) => {
        const newValue = value ? 'active' : 'inactive';
        setSettingsValue({
            ...settingValue,
            personalized_controls: {
                ...settingValue.personalized_controls,
                [child]: newValue,
            },
        });
        onChange({
            personalized_controls: {
                ...settingValue.personalized_controls,
                [child]: newValue,
            },
        });
    };

    const personalizedControlsData = [
        {
            header: 'HLS Play',
            body: 'Enable HLS video streaming for better playback experience.',
            service_name: 'hls_play',
        },
        {
            header: 'Contest Emails',
            body: 'Receive email notifications about contests and competitions.',
            service_name: 'contest_emails',
        },
        {
            header: 'Advertisements',
            body: 'Allow personalized advertisements on your content.',
            service_name: 'ads',
        },
        {
            header: 'Audition',
            body: 'Enable audition features and notifications.',
            service_name: 'audition',
        },
        {
            header: 'Movie Ticket',
            body: 'Enable movie ticket purchasing and management.',
            service_name: 'movie_ticket',
        },
        {
            header: 'Voting',
            body: 'Enable voting features and participation.',
            service_name: 'voting',
        },
        {
            header: 'Acts',
            body: 'Enable acts and performance features.',
            service_name: 'acts',
        },
    ];

    const preferenceRowIcon = (serviceName: string) => {
        const sx = { fontSize: 26 as const };
        switch (serviceName) {
            case 'hls_play':
                return <PlayCircleOutlineOutlinedIcon sx={sx} />;
            case 'contest_emails':
                return <EmojiEventsOutlinedIcon sx={sx} />;
            case 'ads':
                return <CampaignOutlinedIcon sx={sx} />;
            case 'audition':
                return <TheaterComedyOutlinedIcon sx={sx} />;
            case 'movie_ticket':
                return <ConfirmationNumberOutlinedIcon sx={sx} />;
            case 'voting':
                return <HowToVoteOutlinedIcon sx={sx} />;
            case 'acts':
                return <Diversity3OutlinedIcon sx={sx} />;
            default:
                return <PlayCircleOutlineOutlinedIcon sx={sx} />;
        }
    };

    return (
        <ThemeProvider theme={switchTheme}>
            <Box
                style={styles.flexColumn}
                sx={{
                    gap: 3,
                    width: '100%',
                    height: '100%',
                    p: {
                        xs: 2,
                        sm: 0,
                    },
                }}>
                <Box
                    style={styles.flexColumn}
                    sx={{
                        gap: 4,
                        width: '100%',
                    }}>
                    <Box style={styles.flexColumn} sx={{ gap: '10px', maxWidth: '454px' }}>
                        <Typography style={styles.subtitle}>Personalized Controls</Typography>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].tag_white,
                                opacity: 0.65,
                                lineHeight: 1.5,
                                maxWidth: '36rem',
                            }}>
                            Customize your experience with personalized controls and preferences.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: '100%',
                        }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                            {settingValue &&
                                settingValue.personalized_controls &&
                                personalizedControlsData.map((set, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            flexWrap: 'wrap',
                                            p: 3,
                                            borderRadius: 3,
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            bgcolor: 'rgba(39, 39, 42, 0.35)',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': { bgcolor: 'rgba(39, 39, 42, 0.5)' },
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                minWidth: 0,
                                                flex: 1,
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 2,
                                                    flexShrink: 0,
                                                    bgcolor: 'rgba(0,0,0,0.28)',
                                                    border: '1px solid rgba(255,255,255,0.06)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: theme[themeStyle].highlight,
                                                }}>
                                                {preferenceRowIcon(set.service_name)}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontWeight: 700,
                                                        fontSize: '0.9375rem',
                                                        color: theme[themeStyle].primary_white,
                                                        mb: 0.25,
                                                    }}>
                                                    {set.header}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        color: theme[themeStyle].tag_white,
                                                        opacity: 0.65,
                                                        lineHeight: 1.4,
                                                    }}>
                                                    {set.body}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Switch
                                            focusVisibleClassName=".Mui-focusVisible"
                                            disableRipple
                                            checked={
                                                settingValue.personalized_controls[
                                                    set.service_name as keyof typeof settingValue.personalized_controls
                                                ] === 'active'
                                            }
                                            onChange={e => handleOnToggle(e.target.checked, set.service_name)}
                                            sx={{
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        linebreak: {
            width: '100%',
            border: `1px solid ${theme[colorScheme].gray_text}`,
        },
        subtitle: {
            fontSize: 16,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].primary_white,
        },
        text: {
            fontSize: 16,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            textAlign: 'left',
            color: theme[colorScheme].tag_white,
        },
    });

export { AccountPreference };
